import {
  VStack,
  Text,
  Spinner,
  Center,
  Button,
  ButtonGroup,
  IconButton,
  Pagination,
  Box,
} from '@chakra-ui/react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { ConversationItem } from './conversation-item';

import { useState, useEffect, useCallback } from 'react';
import { useWhatsAppEvents } from '@/context/whatsapp-events-context';
import { toaster } from '@/components/ui/toaster';
import { Conversation } from '@/features/conversations/conversations.types';
import { fetchChannel } from '@/features/channels/channels.service';

// TODO: Move to a separate file
const checkNeedWhatsAppConnection = async ({
  channelId,
  isChannelConnected,
  attemptedChannels,
  setAttemptedChannels,
  handleConnectToWhatsApp,
  selectedConversationId,
}: {
  channelId: string;
  selectedConversationId: string;
  isChannelConnected: (channelId: string) => boolean;
  attemptedChannels: Record<string, boolean>;
  setAttemptedChannels: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  handleConnectToWhatsApp: (channelId: string) => Promise<void>;
}) => {
  try {
    // Get channel details
    const { data: channel } = await fetchChannel({ id: channelId });

    // If this is a WhatsApp channel and we haven't connected to it yet
    if (
      channel?.type === 'whatsapp' &&
      !isChannelConnected(channelId) &&
      !attemptedChannels[channelId]
    ) {
      // Mark that we've attempted to connect to this channel
      setAttemptedChannels((prev) => ({
        ...prev,
        [channelId]: true,
      }));

      console.log(
        `[ConversationList] Connecting to WhatsApp channel: ${channelId}`,
      );

      // Connect to the channel
      await handleConnectToWhatsApp(channelId);

      console.log(
        `[ConversationList] Successfully connected to WhatsApp channel: ${channelId}`,
      );
    }
  } catch (error) {
    console.error(
      `Failed to connect to channel for conversation ${selectedConversationId}:`,
      error,
    );
    toaster.create({
      title: 'Connection error',
      description: 'Failed to connect to WhatsApp channel',
      type: 'error',
      duration: 5000,
    });
  }
};

interface ConversationListProps {
  conversations: Conversation[];
  loading: boolean;
  error: Error | null;
  total: number;
  page: number;
  setPage: (page: number) => void;
  perPage: number;
  selectedConversationId?: string;
  onSelectConversation: (conversationId: string) => void;
}

export function ConversationList({
  conversations,
  loading,
  error,
  total,
  page,
  setPage,
  perPage,
  selectedConversationId,
  onSelectConversation,
}: ConversationListProps) {
  // Get channel connection functions from our context
  const { connectChannel, isChannelConnected, activeChannels } =
    useWhatsAppEvents();

  // Track channels we've attempted to connect to
  const [attemptedChannels, setAttemptedChannels] = useState<
    Record<string, boolean>
  >({});

  // Define handleConnectToWhatsApp inside the component, wrapped in useCallback
  const handleConnectToWhatsApp = useCallback(
    async (channelId: string) => {
      try {
        console.log(
          `[ConversationList] Attempting to connect to WhatsApp channel: ${channelId}`,
        );
        await connectChannel(channelId);

        console.log(
          `[ConversationList] Successfully connected to WhatsApp channel: ${channelId}`,
        );
      } catch (error) {
        console.error(
          `[ConversationList] Error in handleConnectToWhatsApp for channel ${channelId}:`,
          error,
        );
        toaster.create({
          title: 'Channel Connection Failed',
          description: `Could not subscribe to events for channel ${channelId}.`,
          type: 'error',
          duration: 5000,
        });
      }
    },
    [connectChannel],
  ); // Remove toast from dependencies

  // Effect to handle WhatsApp channel connection when a conversation is selected
  useEffect(() => {
    if (!selectedConversationId) return;

    // Find the selected conversation in our list
    const conversation = conversations.find(
      (conv) => conv.id === selectedConversationId,
    );
    if (!conversation || !conversation.channel_id) return;

    // Get channel details and ensure channel_id is defined
    const channelId = conversation.channel_id;

    // Check if we need to connect to a WhatsApp channel
    checkNeedWhatsAppConnection({
      channelId,
      isChannelConnected,
      attemptedChannels,
      setAttemptedChannels,
      handleConnectToWhatsApp,
      selectedConversationId,
    });
  }, [
    selectedConversationId,
    conversations,
    connectChannel,
    isChannelConnected,
    attemptedChannels,
    handleConnectToWhatsApp,
  ]);

  // Effect to log active channels for debugging
  useEffect(() => {
    console.log('Active WhatsApp channels:', activeChannels);
  }, [activeChannels]);

  // Handle loading state
  if (loading && page === 1) {
    return (
      <Center py={10}>
        <Spinner size="lg" color="blue.500" />
      </Center>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Center py={10} flexDirection="column" gap={4}>
        <Text color="red.500">Error loading conversations</Text>
        <Button colorScheme="blue" size="sm" onClick={() => setPage(1)}>
          Retry
        </Button>
      </Center>
    );
  }

  return (
    <VStack gap={0} align="inherit" height="100%">
      {/* Conversation list */}
      <Box flex="1" overflowY="auto">
        <VStack gap={0} align="stretch">
          {conversations.length === 0 ? (
            <Center py={10}>
              <Text color="gray.500">No conversations found</Text>
            </Center>
          ) : (
            conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isSelected={conversation.id === selectedConversationId}
                onClick={() => onSelectConversation(conversation.id)}
              />
            ))
          )}
        </VStack>
      </Box>

      {/* Pagination - Always at bottom */}
      <Box
        px={4}
        py={3}
        borderTop="1px"
        borderColor="gray.200"
        flexShrink={0}
        bg="white"
        alignSelf="center"
      >
        <Pagination.Root
          count={total}
          pageSize={perPage}
          page={page}
          onPageChange={(details) => {
            setPage(details.page);
          }}
        >
          <ButtonGroup variant="ghost" size="sm" justifyContent="center">
            <Pagination.PrevTrigger asChild>
              <IconButton>
                <LuChevronLeft />
              </IconButton>
            </Pagination.PrevTrigger>

            <Pagination.Items
              render={(page) => (
                <IconButton variant={{ base: 'ghost', _selected: 'outline' }}>
                  {page.value}
                </IconButton>
              )}
            />

            <Pagination.NextTrigger asChild>
              <IconButton>
                <LuChevronRight />
              </IconButton>
            </Pagination.NextTrigger>
          </ButtonGroup>
        </Pagination.Root>
      </Box>
    </VStack>
  );
}
